// src/services/tiktok-pixel.service.ts

import { privacyConsentService } from "./privacy-consent.service";

type TikTokContent = {
  content_id: string;
  content_type: "product" | "product_group";
  content_name: string;
};

type TikTokEventPayload = {
  contents?: TikTokContent[];
  value?: number;
  currency?: string;
  search_string?: string;
};

type TikTokIdentifyPayload = {
  email?: string | null;
  phone_number?: string | null;
  external_id?: string | null;
};

declare global {
  interface Window {
    ttq?: {
      page: () => void;
      track: (event: string, payload?: Record<string, unknown>) => void;
      identify: (payload: Record<string, string>) => void;
      load: (pixelId: string) => void;
      instance: (pixelId: string) => unknown;
      _i?: unknown;
      _t?: unknown;
      _o?: unknown;
      methods?: string[];
      setAndDefer?: (target: Record<string, unknown>, method: string) => void;
    };
  }
}

const DEFAULT_CURRENCY = "KES";

class TikTokPixelService {
  private initialized = false;
  private pixelId = process.env.REACT_APP_TIKTOK_PIXEL_ID || "";
  private identifiedUsers = new Set<string>();
  private firedEvents = new Set<string>();

  init() {
    if (typeof window === "undefined") return;
    if (this.initialized) return;

    if (!privacyConsentService.canUseMarketing()) {
      return;
    }

    if (!this.pixelId) {
      console.warn("TikTok Pixel not initialized: missing REACT_APP_TIKTOK_PIXEL_ID");
      return;
    }

    // Standard TikTok pixel bootstrap
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((w: any, d: Document, t: string) => {
      w.TiktokAnalyticsObject = t;
      const ttq = (w[t] = w[t] || []);
      ttq.methods = [
        "page",
        "track",
        "identify",
        "instances",
        "debug",
        "on",
        "off",
        "once",
        "ready",
        "alias",
        "group",
        "enableCookie",
        "disableCookie",
        "holdConsent",
        "revokeConsent",
        "grantConsent",
      ];
      ttq.setAndDefer = function (obj: any, method: string) {
        obj[method] = function (...args: unknown[]) {
          (obj as any).push([method, ...args]);
        };
      };

      for (let i = 0; i < ttq.methods.length; i += 1) {
        ttq.setAndDefer(ttq, ttq.methods[i]);
      }

      ttq.instance = function (_pixelId: string) {
        const instances = ttq._i || {};
        const instance = instances[_pixelId] || [];
        for (let i = 0; i < ttq.methods.length; i += 1) {
          ttq.setAndDefer(instance, ttq.methods[i]);
        }
        return instance;
      };

      ttq.load = function (pixelId: string) {
        const script = d.createElement("script");
        script.async = true;
        script.src = "https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=" + pixelId;
        const firstScript = d.getElementsByTagName("script")[0];
        firstScript?.parentNode?.insertBefore(script, firstScript);
      };

      ttq.load(this.pixelId);
      ttq.page();
    })(window, document, "ttq");

    this.initialized = true;
  }

  page() {
    if (!privacyConsentService.canUseMarketing()) return;
    if (!window.ttq) return;
    window.ttq.page();
  }

  async identify(payload: TikTokIdentifyPayload) {
    if (!privacyConsentService.canUseMarketing()) return;
    if (!window.ttq) return;

    const email = payload.email ? await this.sha256(payload.email.trim().toLowerCase()) : undefined;
    const phone_number = payload.phone_number
      ? await this.sha256(payload.phone_number.replace(/\s+/g, ""))
      : undefined;
    const external_id = payload.external_id ? await this.sha256(payload.external_id) : undefined;

    const dedupeKey = external_id || email || phone_number;
    if (dedupeKey && this.identifiedUsers.has(dedupeKey)) return;

    const cleanPayload: Record<string, string> = {};
    if (email) cleanPayload.email = email;
    if (phone_number) cleanPayload.phone_number = phone_number;
    if (external_id) cleanPayload.external_id = external_id;

    if (Object.keys(cleanPayload).length === 0) return;

    window.ttq.identify(cleanPayload);

    if (dedupeKey) {
      this.identifiedUsers.add(dedupeKey);
    }
  }

  track(event: string, payload?: TikTokEventPayload, dedupeKey?: string) {
    if (!privacyConsentService.canUseMarketing()) return;
    if (!window.ttq) return;
    if (dedupeKey && this.firedEvents.has(dedupeKey)) return;

    const cleanPayload: Record<string, unknown> = {};

    if (payload?.contents?.length) cleanPayload.contents = payload.contents;

    const hasPositiveValue =
      typeof payload?.value === "number" && Number.isFinite(payload.value) && payload.value > 0;

    if (hasPositiveValue) {
      cleanPayload.value = payload!.value;
      if (payload?.currency) cleanPayload.currency = payload.currency;
    }

    if (payload?.search_string) cleanPayload.search_string = payload.search_string;

    window.ttq.track(event, cleanPayload);

    if (dedupeKey) {
      this.firedEvents.add(dedupeKey);
    }
  }

  trackViewContent(
    content: TikTokContent,
    value?: number,
    currency = DEFAULT_CURRENCY,
    dedupeKey?: string
  ) {
    this.track(
      "ViewContent",
      {
        contents: [content],
        ...(typeof value === "number" ? { value } : {}),
        currency,
      },
      dedupeKey
    );
  }

  trackAddToCart(
    content: TikTokContent,
    value: number,
    currency = DEFAULT_CURRENCY,
    dedupeKey?: string
  ) {
    this.track(
      "AddToCart",
      {
        contents: [content],
        value,
        currency,
      },
      dedupeKey
    );
  }

  trackInitiateCheckout(
    content: TikTokContent,
    value: number,
    currency = DEFAULT_CURRENCY,
    dedupeKey?: string
  ) {
    this.track(
      "InitiateCheckout",
      {
        contents: [content],
        value,
        currency,
      },
      dedupeKey
    );
  }

  trackAddPaymentInfo(
    content: TikTokContent,
    value: number,
    currency = DEFAULT_CURRENCY,
    dedupeKey?: string
  ) {
    this.track(
      "AddPaymentInfo",
      {
        contents: [content],
        value,
        currency,
      },
      dedupeKey
    );
  }

  trackCompleteRegistration(
    content: TikTokContent,
    value = 0,
    currency = DEFAULT_CURRENCY,
    dedupeKey?: string
  ) {
    this.track(
      "CompleteRegistration",
      {
        contents: [content],
        value,
        currency,
      },
      dedupeKey
    );
  }

  trackPurchase(
    content: TikTokContent,
    value: number,
    currency = DEFAULT_CURRENCY,
    dedupeKey?: string
  ) {
    this.track(
      "Purchase",
      {
        contents: [content],
        value,
        currency,
      },
      dedupeKey
    );
  }

  private async sha256(value: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }
}

export const tiktokPixelService = new TikTokPixelService();
